import React, { createContext, useContext, useReducer, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

// Estado inicial
const initialState = {
  users: [],
  currentUser: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
    count: 0
  },
  filters: {
    search: '',
    role: '',
    isActive: ''
  }
};

// Tipos de acciones
const USER_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  GET_USERS_SUCCESS: 'GET_USERS_SUCCESS',
  GET_USER_SUCCESS: 'GET_USER_SUCCESS',
  CREATE_USER_SUCCESS: 'CREATE_USER_SUCCESS',
  UPDATE_USER_SUCCESS: 'UPDATE_USER_SUCCESS',
  DELETE_USER_SUCCESS: 'DELETE_USER_SUCCESS',
  TOGGLE_USER_STATUS_SUCCESS: 'TOGGLE_USER_STATUS_SUCCESS',
  USER_ERROR: 'USER_ERROR',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SET_FILTERS: 'SET_FILTERS',
  CLEAR_CURRENT_USER: 'CLEAR_CURRENT_USER'
};

// Reducer
const userReducer = (state, action) => {
  switch (action.type) {
    case USER_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case USER_ACTIONS.GET_USERS_SUCCESS:
      return {
        ...state,
        users: action.payload.users,
        pagination: {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
          count: action.payload.count
        },
        loading: false,
        error: null
      };
    
    case USER_ACTIONS.GET_USER_SUCCESS:
      return {
        ...state,
        currentUser: action.payload,
        loading: false,
        error: null
      };
    
    case USER_ACTIONS.CREATE_USER_SUCCESS:
      return {
        ...state,
        users: [action.payload, ...state.users],
        loading: false,
        error: null
      };
    
    case USER_ACTIONS.UPDATE_USER_SUCCESS:
      return {
        ...state,
        users: state.users.map(user =>
          user._id === action.payload.id ? action.payload : user
        ),
        currentUser: state.currentUser && state.currentUser._id === action.payload.id 
          ? action.payload 
          : state.currentUser,
        loading: false,
        error: null
      };
    
    case USER_ACTIONS.DELETE_USER_SUCCESS:
      return {
        ...state,
        users: state.users.filter(user => user._id !== action.payload),
        loading: false,
        error: null
      };
    
    case USER_ACTIONS.TOGGLE_USER_STATUS_SUCCESS:
      return {
        ...state,
        users: state.users.map(user =>
          user._id === action.payload.id 
            ? { ...user, isActive: action.payload.isActive }
            : user
        ),
        loading: false,
        error: null
      };
    
    case USER_ACTIONS.USER_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case USER_ACTIONS.CLEAR_ERRORS:
      return {
        ...state,
        error: null
      };
    
    case USER_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      };
    
    case USER_ACTIONS.CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null
      };
    
    default:
      return state;
  }
};

// Crear contexto
const UserContext = createContext();

// Hook personalizado
export const useUsers = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUsers debe ser usado dentro de UserProvider');
  }
  return context;
};

// Provider
export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  // Obtener usuarios
  const getUsers = useCallback(async (page = 1, limit = 10) => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const paramObj = {page: page.toString(), limit: limit.toString()};
      if (state.filters.search) paramObj.search = state.filters.search;
      if (state.filters.role) paramObj.role = state.filters.role;
      if (state.filters.isActive !== "") paramObj.isActive = state.filters.isActive;
      const params = new URLSearchParams(paramObj);
      console.log("📤 Parámetros finales:", params.toString());

      const res = await axios.get(`/api/users?${params}`);
      
      dispatch({
        type: USER_ACTIONS.GET_USERS_SUCCESS,
        payload: res.data
      });
      
      return { success: true, users: res.data.users };
    } catch (error) {
      const message = error.response?.data?.message || 'Error obteniendo usuarios';
      
      dispatch({
        type: USER_ACTIONS.USER_ERROR,
        payload: message
      });
      
      toast.error(message);
      return { success: false, message };
    }
  }, [state.filters]);

  // Obtener usuario por ID
  const getUser = async (id) => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const res = await axios.get(`/api/users/${id}`);
      
      dispatch({
        type: USER_ACTIONS.GET_USER_SUCCESS,
        payload: res.data.user
      });
      
      return { success: true, user: res.data.user };
    } catch (error) {
      const message = error.response?.data?.message || 'Error obteniendo usuario';
      
      dispatch({
        type: USER_ACTIONS.USER_ERROR,
        payload: message
      });
      
      toast.error(message);
      return { success: false, message };
    }
  };

  // Crear usuario
  const createUser = async (userData) => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const res = await axios.post('/api/users', userData);
      
      dispatch({
        type: USER_ACTIONS.CREATE_USER_SUCCESS,
        payload: res.data.user
      });
      
      toast.success(res.data.message || 'Usuario creado exitosamente');
      return { success: true, user: res.data.user };
    } catch (error) {
      const message = error.response?.data?.message || 'Error creando usuario';
      
      dispatch({
        type: USER_ACTIONS.USER_ERROR,
        payload: message
      });
      
      toast.error(message);
      return { success: false, message };
    }
  };

  // Actualizar usuario
  const updateUser = async (id, userData) => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const res = await axios.put(`/api/users/${id}`, userData);
      
      dispatch({
        type: USER_ACTIONS.UPDATE_USER_SUCCESS,
        payload: res.data.user
      });
      
      toast.success(res.data.message || 'Usuario actualizado exitosamente');
      return { success: true, user: res.data.user };
    } catch (error) {
      const message = error.response?.data?.message || 'Error actualizando usuario';
      
      dispatch({
        type: USER_ACTIONS.USER_ERROR,
        payload: message
      });
      
      toast.error(message);
      return { success: false, message };
    }
  };

  // Eliminar usuario
  const deleteUser = async (id) => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const res = await axios.delete(`/api/users/${id}`);
      
      dispatch({
        type: USER_ACTIONS.DELETE_USER_SUCCESS,
        payload: id
      });
      
      toast.success(res.data.message || 'Usuario eliminado exitosamente');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Error eliminando usuario';
      
      dispatch({
        type: USER_ACTIONS.USER_ERROR,
        payload: message
      });
      
      toast.error(message);
      return { success: false, message };
    }
  };

  // Cambiar estado del usuario
  const toggleUserStatus = async (id) => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const res = await axios.patch(`/api/users/${id}/toggle-status`);
      
      dispatch({
        type: USER_ACTIONS.TOGGLE_USER_STATUS_SUCCESS,
        payload: {
          id,
          isActive: res.data.user.isActive
        }
      });
      
      toast.success(res.data.message);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Error cambiando estado del usuario';
      
      dispatch({
        type: USER_ACTIONS.USER_ERROR,
        payload: message
      });
      
      toast.error(message);
      return { success: false, message };
    }
  };

  // Establecer filtros
  const setFilters = (filters) => {
    dispatch({
      type: USER_ACTIONS.SET_FILTERS,
      payload: filters
    });
  };

  // Limpiar errores
  const clearErrors = () => {
    dispatch({ type: USER_ACTIONS.CLEAR_ERRORS });
  };

  // Limpiar usuario actual
  const clearCurrentUser = () => {
    dispatch({ type: USER_ACTIONS.CLEAR_CURRENT_USER });
  };

  const value = {
    ...state,
    getUsers,
    getUser,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    setFilters,
    clearErrors,
    clearCurrentUser
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
